/**
 * Per-user success graphic helpers.
 *
 * The celebration image on success screens ("Logged and Loaded!" and the
 * Footage Search "bundle ready!" view) can be personalized per user. When a
 * user has no graphic set, this shared default trio image is shown instead.
 */

/** The shared, app-wide default celebration graphic (CloudFront-hosted). */
export const DEFAULT_SUCCESS_GRAPHIC =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663298559824/nnYijT6prcHrsoyRRDyzsb/footage-rambo-trio-final-v2_d93fbe62.webp";

// Keep in sync with `successGraphicSchema` on the server (routers.ts). A
// data-URL string is ~4/3 the size of the raw file, so this ≈ a 6 MB file.
export const MAX_GRAPHIC_DATA_URL_CHARS = 8_000_000;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Could not load image"));
    img.src = src;
  });
}

/**
 * Downscale an image File to a JPEG data-URL, preserving aspect ratio and
 * capping the longest edge at `maxEdge`. Keeps the encoded payload small
 * enough to store inline on the user row (well under the 8 MB server ceiling).
 * Success graphics are landscape, so unlike provider photos this does not crop.
 */
export async function downscaleImageToDataUrl(
  file: File,
  maxEdge = 1024,
  quality = 0.85,
): Promise<string> {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrl);
    const { naturalWidth: w, naturalHeight: h } = image;
    if (!w || !h) throw new Error("Image has no dimensions");

    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const outW = Math.max(1, Math.round(w * scale));
    const outH = Math.max(1, Math.round(h * scale));

    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not available");
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(image, 0, 0, outW, outH);
    return canvas.toDataURL("image/jpeg", quality);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/** Read a File to a data-URL without re-encoding — preserves GIF animation. */
function fileToRawDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read the image file"));
    reader.readAsDataURL(file);
  });
}

/**
 * Turn a chosen image File into a data-URL suitable for a success graphic.
 * Animated GIFs are kept byte-for-byte so the animation survives (canvas
 * re-encoding would flatten them to a single frame); every other image is
 * downscaled to a compact JPEG. Throws a user-friendly error if an animated
 * GIF is too large to store inline.
 */
export async function fileToSuccessGraphicDataUrl(file: File): Promise<string> {
  if (file.type === "image/gif") {
    const dataUrl = await fileToRawDataUrl(file);
    if (dataUrl.length > MAX_GRAPHIC_DATA_URL_CHARS) {
      throw new Error(
        "That GIF is too large to save — please use one under ~6 MB (a shorter clip or smaller dimensions).",
      );
    }
    return dataUrl;
  }
  return downscaleImageToDataUrl(file);
}
